<template>
  <Modal
    :title="editModalTitle"
    bottom-close-button-label="保存"
    :isCloseButtonDisabled="validateEditing.length > 0"
    @close="save"
    @dismiss="$emit('close')"
  >
    <template v-slot:body>
      <div>
        <div class="form-group" v-if="editMode === 'create' || editMode === 'edit'">
          <label class="form-control-label">事業所</label>
          <select
            class="form-control"
            v-model="state.editForm.group2Id"
            @change="state.editForm.groupId = null"
          >
            <option v-for="e in groups2" :key="e.id" :value="e.id">
              {{e.name}}
            </option>
          </select>
        </div>
        <div class="form-group" v-if="editMode === 'create' || editMode === 'edit'">
          <label class="form-control-label">部署</label>
          <select class="form-control" v-model="state.editForm.groupId" :disabled="!selectedGroup2">
            <option v-for="e in filteredGroups3" :key="e.id" :value="e.id">
              {{e.name}}
            </option>
          </select>
        </div>
        <div class="form-group" v-if="editMode === 'create' || editMode === 'edit'">
          <label class="form-control-label">
            ユーザーID
          </label>
          <input
            type="text"
            class="form-control"
            v-model="state.editForm.username"
            :disabled="editMode === 'edit'"
          />
        </div>
        <div class="form-group" v-if="editMode === 'create' || editMode === 'edit'">
          <label class="form-control-label">
            ユーザー名
          </label>
          <input type="text" class="form-control" v-model="state.editForm.displayName">
        </div>
        <div class="form-group" v-if="editMode === 'create' || editMode === 'edit'">
          <label class="form-control-label">
            権限
          </label>
          <div v-if="isLoginUser">
            <span class="text-danger">
              ※自分の権限を変更することはできません
            </span>
          </div>
          <div v-if="isStrongerRoleThanMe">
            <span class="text-danger">
              ※このユーザーの権限を変更することはできません
            </span>
          </div>
          <select
            class="form-control"
            v-model="state.editForm.role"
            :disabled="isStrongerRoleThanMe || isLoginUser"
          >
            <option v-for="e in selectableRoles" :key="e.key" :value="e.key">
              {{e.val}}
            </option>
          </select>
        </div>
        <div class="form-group" v-if="editMode === 'create' || editMode === 'editPassword'">
          <label class="form-control-label">
            パスワード
          </label>
          <input
            type="password"
            class="form-control"
            v-model="state.editForm.password"
            autocomplete="new-password"
          >
          <span class="ib ml8 text-warning password-condition-warning">
            <div>※パスワードは8文字以上で入力してください</div>
            <div>※パスワードには英小文字、英大文字、数字、記号から3種類以上の文字種を含めてください。</div>
          </span>
        </div>
        <div class="form-group" v-if="editMode === 'create' || editMode === 'editPassword'">
          <label class="form-control-label">
            パスワード確認
          </label>
          <input
            type="password"
            class="form-control"
            v-model="state.editForm.passwordConfirmation"
            autocomplete="new-password"
          >
        </div>
        <div class="form-group errors">
          <div class="error" v-if="passwordErrorMsg != null && state.createOrUpdatePasswordOkPushed">
            {{passwordErrorMsg}}
          </div>
          <div class="error" v-for="(e, i) in httpErrorMsgs" :key="i">
            {{e}}
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import type { AdminGroup2 } from 'src/models/group2';
import type { AdminGroup3 } from 'src/models/group3';
import type { Lov } from 'src/models/master';
import type { AdminUser } from 'src/models/user';
import type {
  AdminUserCreateParams,
  AdminUserPasswordUpdateParams,
  AdminUserUpdateParams,
} from 'src/models/apis/user/adminUserRequest';
import { useAuth } from 'src/composables/useAuth';

const PASSWORD_MIN_LENGTH = 8;

const props = defineProps<{
  editMode: 'create' | 'edit' | 'editPassword';
  editingUser: AdminUser | null;
  groups2: AdminGroup2[];
  groups3: AdminGroup3[];
  selectableRoles: Lov[];
  httpErrorMsgs: string[];
}>();

const emit = defineEmits<{
  (e: 'create', createData: AdminUserCreateParams): void;
  (e: 'updateProfile', id: number, updateData: AdminUserUpdateParams): void;
  (e: 'updatePassword', id: number, updateData: AdminUserPasswordUpdateParams): void;
  (e: 'close'): void;
}>();

interface UserEditForm {
  id: number;
  group2Id: number | null;
  groupId: number | null;
  username: string;
  displayName: string;
  role: string | null;
  password: string;
  passwordConfirmation: string;
}

const initEditingState = (): UserEditForm => {
  return {
    id: 0,
    group2Id: null,
    groupId: null,
    username: '',
    displayName: '',
    role: null,
    password: '',
    passwordConfirmation: '',
  };
};

// パスワード変更では groupId は user.groupId、編集では groupInfo.g3id を使う（既存挙動を踏襲）
const buildEditForm = (user: AdminUser, groupId: number | null): UserEditForm => {
  return {
    id: user.id,
    group2Id: user.groupInfo.g2id,
    groupId,
    username: user.username,
    displayName: user.displayName,
    role: user.role.toString(),
    password: '',
    passwordConfirmation: '',
  };
};

const state = reactive<{ editForm: UserEditForm; createOrUpdatePasswordOkPushed: boolean }>({
  editForm: initEditingState(),
  createOrUpdatePasswordOkPushed: false,
});

const auth = useAuth();

const isValidPasswordString = (password: string): boolean => {
  const regAlphaLower = /[a-z]/;
  const regAlphaUpper = /[A-Z]/;
  const regNumber = /[0-9]/;
  const regSymbol = /[!@#$%^&*(){}[\]:;"'<>,.?/~`_+\-=|\\ ]/;
  // 3種類以上の文字種を含めることを強制する.
  let numStrKinds = 0;
  if (password.match(regAlphaLower)) { numStrKinds++; }
  if (password.match(regAlphaUpper)) { numStrKinds++; }
  if (password.match(regNumber)) { numStrKinds++; }
  if (password.match(regSymbol)) { numStrKinds++; }
  return numStrKinds >= 3;
};

const requiresProfileFields = computed(() => {
  return props.editMode === 'create' || props.editMode === 'edit';
});
const requiresPasswordFields = computed(() => {
  return props.editMode === 'create' || props.editMode === 'editPassword';
});
const filteredGroups3 = computed<AdminGroup3[]>(() => {
  return props.groups3.filter(group3 => group3.parentId === state.editForm.group2Id);
});
const validateEditing = computed(() => {
  const errorMsgs: string[] = [];
  if (requiresProfileFields.value &&
    (!state.editForm.group2Id || !Number.isInteger(state.editForm.group2Id))
  ) {
    errorMsgs.push('事業所は選択必須です');
  }
  if (requiresProfileFields.value &&
    (!state.editForm.groupId || !Number.isInteger(state.editForm.groupId))
  ) {
    errorMsgs.push('部署は選択必須です');
  }
  if (requiresProfileFields.value &&
    (!state.editForm.username || state.editForm.username.length <= 0)
  ) {
    errorMsgs.push('ユーザーIDは必須です');
  }
  if (requiresProfileFields.value &&
    (!state.editForm.displayName || state.editForm.displayName.length <= 0)
  ) {
    errorMsgs.push('ユーザー名は必須です');
  }
  if (requiresProfileFields.value &&
    (!state.editForm.role || !Number.isInteger(parseInt(state.editForm.role)))
  ) {
    errorMsgs.push('権限は必須です');
  }
  if (requiresPasswordFields.value) {
    if (!state.editForm.password || state.editForm.password.length < PASSWORD_MIN_LENGTH) {
      errorMsgs.push(`パスワードは${PASSWORD_MIN_LENGTH}文字以上にしてください`);
    }
    if (state.editForm.password && !isValidPasswordString(state.editForm.password)) {
      errorMsgs.push('パスワードには英小文字、英大文字、数字、記号から3種類以上の文字種を含めてください。');
    }
  }
  return errorMsgs;
});
const passwordErrorMsg = computed(() => {
  if (requiresPasswordFields.value &&
    state.editForm.password !== state.editForm.passwordConfirmation) {
    return 'パスワードが一致しません。';
  }
  return null;
});
const selectedGroup2 = computed(() => {
  return state.editForm.group2Id != null;
});
const editModalTitle = computed(() => {
  if (props.editMode === 'create') {
    return '新規作成';
  } else if (props.editMode === 'editPassword') {
    return 'パスワード変更';
  } else {
    return '編集';
  }
});
const isStrongerRoleThanMe = computed(() => {
  if (!auth.user) return false;
  if (!state.editForm.id || !state.editForm.role) return false;

  const otherUserRole = parseInt(state.editForm.role);
  return otherUserRole < auth.user.role;
});
const isLoginUser = computed(() => {
  return !!auth.user && auth.user.id === state.editForm.id;
});

const save = (): void => {
  if (requiresPasswordFields.value) {
    state.createOrUpdatePasswordOkPushed = true;
    if (passwordErrorMsg.value) return;
  }
  if (props.editMode === 'edit') {
    if (!state.editForm.id ||
      !state.editForm.username ||
      !state.editForm.displayName ||
      !state.editForm.role ||
      !state.editForm.groupId
    ) {
      return;
    }
    emit('updateProfile', state.editForm.id, {
      username: state.editForm.username,
      display_name: state.editForm.displayName || '',
      role: parseInt(state.editForm.role),
      group_id: state.editForm.groupId,
    });
  } else if (props.editMode === 'editPassword') {
    if (!state.editForm.id) return;

    emit('updatePassword', state.editForm.id, {
      password: state.editForm.password || '',
      password_confirmation: state.editForm.passwordConfirmation || '',
    });
  } else {
    if (!state.editForm.role ||
      !state.editForm.groupId ||
      !state.editForm.password ||
      !state.editForm.passwordConfirmation
    ) {
      return;
    }
    emit('create', {
      id: state.editForm.id,
      username: state.editForm.username,
      display_name: state.editForm.displayName,
      role: parseInt(state.editForm.role),
      group_id: state.editForm.groupId,
      password: state.editForm.password,
      password_confirmation: state.editForm.passwordConfirmation,
    });
  }
};

watch(() => [props.editMode, props.editingUser] as const, ([editMode, editingUser]) => {
  state.createOrUpdatePasswordOkPushed = false;
  if (editMode === 'create' || !editingUser) {
    state.editForm = initEditingState();
    return;
  }
  const groupId = editMode === 'editPassword' ? editingUser.groupId : editingUser.groupInfo.g3id;
  state.editForm = buildEditForm(editingUser, groupId);
}, { immediate: true });
</script>

<style lang="scss" scoped>
.password-condition-warning {
  font-size: 0.9rem;
}
</style>
